import { clear, h } from '../core/dom'
import { haptic } from '../core/haptics'
import { go } from '../core/router'
import { getSettings, updateSettings, type Reason } from '../core/store'
import { setLang, t, type Lang } from '../i18n'
import { dots, tile } from './shared'

/**
 * Five screens, and the third one is the reason the other four exist.
 *
 * The taper warning is not a legal disclaimer buried in a footer. It is a step
 * you cannot advance past without ticking a box, because the single most
 * dangerous thing a user of this app can do is decide tonight to stop taking
 * their medication.
 */
export function renderOnboarding(root: HTMLElement): void {
  let step = 0
  let reason: Reason = getSettings().reason
  let acked = false
  let ritualTime = getSettings().ritualTime

  const paint = (): void => {
    clear(root)
    const d = t()
    const body = h('div', { class: 'stack-lg fade-in grow', style: { justifyContent: 'center' } })

    const nav = (label: string, enabled: boolean, onClick: () => void): HTMLElement =>
      h(
        'button',
        {
          class: 'btn primary block',
          disabled: !enabled,
          onclick: () => {
            haptic.tap()
            onClick()
          },
        },
        label,
      )

    switch (step) {
      case 0: {
        body.append(
          h('div', { class: 'center', style: { fontSize: '3.4rem' } }, '🌙'),
          h('div', { class: 'stack-sm center' }, h('h1', {}, d.onboarding.step1Title)),
          h('p', { class: 'lede' }, d.onboarding.step1Body),
          h('p', { class: 'faint center' }, d.onboarding.step1Sub),
          h(
            'div',
            { class: 'seg', style: { alignSelf: 'center' } },
            ...(['en', 'fa'] as Lang[]).map((code) =>
              h(
                'button',
                {
                  class: getSettings().lang === code ? 'on' : '',
                  onclick: () => {
                    setLang(code)
                    updateSettings({ lang: code })
                    paint()
                  },
                },
                code === 'fa' ? 'فارسی' : 'English',
              ),
            ),
          ),
          nav(d.common.continue, true, () => {
            step = 1
            paint()
          }),
        )
        break
      }

      case 1: {
        body.append(
          h('h1', {}, d.onboarding.step2Title),
          h('p', { class: 'lede' }, d.onboarding.step2Body),
          h('p', { class: 'lede' }, d.onboarding.step2Body2),
          nav(d.common.continue, true, () => {
            step = 2
            paint()
          }),
        )
        break
      }

      case 2: {
        const ack = h('button', {
          class: `tile ${acked ? 'selected' : ''}`,
          onclick: () => {
            acked = !acked
            haptic.tap()
            paint()
          },
        })
        ack.append(
          h('span', { class: 'glyph' }, acked ? '✓' : '○'),
          h('span', { class: 'label grow' }, d.onboarding.step3Ack),
        )

        body.append(
          h('h1', {}, d.onboarding.step3Title),
          h(
            'div',
            { class: 'notice' },
            h('p', {}, d.onboarding.step3Body),
            h('p', { style: { marginTop: '10px' } }, d.onboarding.step3Body2),
          ),
          ack,
          nav(d.common.continue, acked, () => {
            updateSettings({ acknowledgedTaperWarning: true })
            step = 3
            paint()
          }),
        )
        break
      }

      case 3: {
        const reasons: Array<[Reason, string, string, string]> = [
          ['insomnia', '🌑', d.onboarding.reasonInsomnia, d.onboarding.reasonInsomniaSub],
          ['tapering', '📉', d.onboarding.reasonTapering, d.onboarding.reasonTaperingSub],
          ['forced', '⚡', d.onboarding.reasonForced, d.onboarding.reasonForcedSub],
          ['curious', '👋', d.onboarding.reasonCurious, d.onboarding.reasonCuriousSub],
        ]
        body.append(
          h('h1', {}, d.onboarding.step4Title),
          h('p', {}, d.onboarding.step4Body),
          h(
            'div',
            { class: 'stack-sm' },
            ...reasons.map(([id, glyph, label, sub]) =>
              tile(glyph, label, sub, () => {
                reason = id
                updateSettings({ reason: id })
                // Someone cut off without a taper gets the urgent-care message
                // immediately, before anything else in the app.
                step = id === 'forced' ? 3.5 : 4
                paint()
              }, reason === id ? 'selected' : ''),
            ),
          ),
        )
        break
      }

      case 3.5: {
        body.append(
          h('h1', {}, d.onboarding.forcedWarnTitle),
          h(
            'div',
            { class: 'notice' },
            h('p', {}, d.onboarding.forcedWarnBody),
            h('p', { style: { marginTop: '10px' } }, d.onboarding.forcedWarnBody2),
          ),
          nav(d.common.continue, true, () => {
            step = 4
            paint()
          }),
        )
        break
      }

      default: {
        const input = h('input', {
          type: 'time',
          value: ritualTime,
          onchange: (e: Event) => {
            ritualTime = (e.target as HTMLInputElement).value || '22:00'
          },
        })
        body.append(
          h('div', { class: 'center', style: { fontSize: '2.6rem' } }, '🕙'),
          h('h1', { class: 'center' }, d.onboarding.step5Title),
          h('p', { class: 'center' }, d.onboarding.step5Body),
          h('div', {}, input),
          nav(d.onboarding.ready, true, () => {
            haptic.complete()
            updateSettings({ ritualTime, reason, onboarded: true })
            go('home')
          }),
        )
      }
    }

    root.append(body, h('div', { style: { paddingTop: '22px' } }, dots(5, Math.floor(step))))
  }

  paint()
}
